import { de } from 'date-fns/locale';
import { t } from 'i18next';
import { StringKeys } from 'src/types/base';
import { CHAT_STEP_NAME, TOOLS_LIST } from 'src/types/enum';
import logger from 'src/utils/logger';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { ActionsTemplates } from '../../Actions/ActionsTemplates';

export interface ChatStep {
  taskId: string;
  label: string;
  content: StringKeys;
  meta: Record<string, any>;
}
export function getFormattedChatMessageSteps(message) {
  if (message?.chatMessageSteps?.length > 0) {
    return message.chatMessageSteps;
  }

  const toolName = message?.tool_name;
  let chatSteps = [];

  if (
    message &&
    ![TOOLS_LIST.PLANNER, TOOLS_LIST.CODE_EXECUTOR].includes(toolName) &&
    message?.search_phrase
  ) {
    chatSteps = [
      {
        label: getSearchPhrase(message)
      }
    ];
  } else if (
    [TOOLS_LIST.PLANNER, TOOLS_LIST.CODE_EXECUTOR].includes(toolName) &&
    message.additional_steps &&
    Object.keys(message.additional_steps).length > 0
  ) {
    const { task_info = {}, task_result = {} } = message.additional_steps;
    const sortedTaskIds = Object.keys(task_info)
      .map((id) => parseInt(id))
      .sort((a, b) => a - b);

    const lastTaskId = sortedTaskIds[sortedTaskIds.length - 1];
    const taskToolName = task_info[lastTaskId]?.tool_name;
    const shouldDefaultStepSkipped = [
      TOOLS_LIST.FINALIZER,
      TOOLS_LIST.JOINNER,
      TOOLS_LIST.DEFAULT
    ].includes(taskToolName);

    chatSteps = sortedTaskIds.reduce((steps, id) => {
      const label = getSearchPhrase({
        response:
          task_result[id]?.content?.search_phrase || task_info[id]?.description,
        tool_name: task_info[id]?.tool_name
      });
      if (label) {
        steps.push({
          label,
          content:
            sortedTaskIds.length > 1 && id !== lastTaskId
              ? {
                  response: task_result[id]?.content?.response || '',
                  meta: task_result[id]?.content || {}
                }
              : undefined
        });
      }
      return steps;
    }, []);
  }

  return chatSteps;
}

export function getFormattedStreamingChatMessageSteps(
  data: StringKeys,
  planStepsCount: number
) {
  let chatSteps = [];
  if (
    data?.tool_name !== TOOLS_LIST.PLANNER &&
    [CHAT_STEP_NAME.TOOL_SELECTED, CHAT_STEP_NAME.TOOL_INPUT].includes(
      data?.step_name
    )
  ) {
    const searchPhrase = getSearchPhrase(data);
    if (searchPhrase) {
      chatSteps = [
        {
          taskId: data?.task_id,
          label: searchPhrase
        }
      ];
    }
  }
  if (
    data?.tool_name === TOOLS_LIST.CODE_EXECUTOR &&
    data?.step_name === CHAT_STEP_NAME.TOOL_SELECTED
  ) {
    let response = data?.response;
    try {
      response = JSON.parse(response);
    } catch (error) {
      response = {};
    }
    let codeSnippet = response?.args?.code_snippet || response?.code_snippet;
    let language = response?.args?.language || 'python';
    chatSteps = [
      {
        taskId: data?.task_id,
        label: 'Executing code',
        content: { response: `\`\`\`${language}\n${codeSnippet}\n\`\`\`` }
      }
    ];
  }
  return chatSteps;
}

function getSearchPhrase(data: StringKeys): string {
  const { tool_name, search_phrase, response } = data;
  let parsedResponse = {};
  try {
    parsedResponse = JSON.parse(response);
  } catch (error) {
    parsedResponse = { query: response };
  }
  if (ActionsTemplates[tool_name]) {
    return ActionsTemplates[tool_name].displayName;
  }

  // Static-phrase tools: no search query to append.
  if (tool_name === TOOLS_LIST.ENTERPRISE_LIST_DOCUMENTS) {
    return t(T.listingRelevantDocuments);
  }
  if (tool_name === TOOLS_LIST.WEB_FETCH) {
    return t(T.retrievingWebpageContent);
  }

  let baseText: string;
  switch (tool_name) {
    case TOOLS_LIST.ENTERPRISE:
    // Legacy tool name kept so historical chats keep their step label.
    case 'enterprise':
      baseText = t(T.searchingEnterpriseKnowledge);
      break;
    case TOOLS_LIST.ATTACHMENTS:
      baseText = t(T.searchingAttachments);
      break;
    case TOOLS_LIST.JOINNER:
    case TOOLS_LIST.DEFAULT:
      baseText = t(T.executing);
      break;
    case TOOLS_LIST.WEB_SEARCH:
      baseText = t(T.searchingWeb);
      break;
    case TOOLS_LIST.NANO_BANANA:
      baseText = t(T.generatingImage);
      break;
    default:
      return undefined;
  }

  return `${baseText} "${
    search_phrase || parsedResponse?.query || parsedResponse?.args?.query || ''
  }"`;
}

export function createChatStepsUpdater(chatSteps: ChatStep[]) {
  const taskIdIndexMap = new Map<string, number>();

  function rebuildIndex() {
    taskIdIndexMap.clear();
    for (let i = 0; i < chatSteps.length; i++) {
      taskIdIndexMap.set(chatSteps[i].taskId, i);
    }
  }

  rebuildIndex();
  function appendToResponse(task_id: string, chunk: string) {
    const index = taskIdIndexMap.get(task_id);
    if (index === undefined) {
      logger.warn(`[appendToResponse] task_id "${task_id}" not found.`);
      return;
    }

    const step = chatSteps[index];

    if (!step.content) {
      step.content = { response: '' };
    }

    if (typeof step.content.response !== 'string') {
      step.content.response = '';
    }

    step.content.response += chunk;

    return chatSteps;
  }
  function update(
    task_id: string,
    updatedFields: Partial<Omit<ChatStep, 'task_id' | 'content'>> & {
      content?: Partial<Omit<ChatStep['content'], 'response'>>;
    }
  ) {
    const index = taskIdIndexMap.get(task_id);
    if (index === undefined) {
      logger.warn(`[updateChatStep] task_id "${task_id}" not found.`);
      return;
    }

    const step = chatSteps[index];

    if (updatedFields.content) {
      step.content = {
        ...step.content,
        ...updatedFields.content
      };
    }
    return chatSteps;
  }

  return {
    appendToResponse,
    update,
    rebuild: rebuildIndex
  };
}

function getToolCallSearchPhrase(toolCall): string | undefined {
  const { name, args } = toolCall || {};
  const search_phrase = args?.search_phrase || args?.query || '';
  return getSearchPhrase({ tool_name: name, search_phrase });
}

function normalizeAiContent(aiContent: string | any[]): string {
  if (Array.isArray(aiContent)) {
    return aiContent
      .filter((part) => part?.type === 'text' && typeof part.text === 'string')
      .map((part) => part.text)
      .join('');
  }
  return aiContent || '';
}

function buildToolCallStep(toolCall, aiContent: string | any[]) {
  const { name, args, id } = toolCall || {};
  const textContent = normalizeAiContent(aiContent);

  if (name === TOOLS_LIST.CODE_EXECUTOR) {
    const codeSnippet = args?.code_snippet || '';
    const language = args?.language || 'python';
    return {
      label: t(T.executing),
      content: {
        response: `${textContent || ''}${
          codeSnippet ? `\n\`\`\`${language}\n${codeSnippet}\n\`\`\`` : ''
        }`,
        meta: {
          language
        }
      }
    };
  }

  const label = getToolCallSearchPhrase(toolCall);
  if (!label) {
    return null;
  }

  return {
    label,
    content: {
      response: textContent || '',
      meta: {}
    }
  };
}

export function getFormattedAgentChatSteps(message) {
  if (message?.chatMessageSteps?.length > 0) {
    return message.chatMessageSteps;
  }

  if (
    !Array.isArray(message?.additional_steps) ||
    message.additional_steps.length === 0
  ) {
    return [];
  }

  const chatSteps = [];
  const stepsByToolCallId = new Map<string, any>();

  message.additional_steps.forEach((step) => {
    if (step.tool_calls && step.tool_calls.length > 0) {
      step.tool_calls.forEach((toolCall) => {
        const formattedStep = buildToolCallStep(toolCall, step.content);
        if (formattedStep) {
          stepsByToolCallId.set(toolCall.id, formattedStep);
          chatSteps.push(formattedStep);
        }
      });
    } else if (step.type === 'tool' && step.tool_call_id) {
      const matchingStep = stepsByToolCallId.get(step.tool_call_id);
      if (matchingStep && !matchingStep.content.result) {
        matchingStep.content.result = step.content || '';
        matchingStep.content.meta.status = step.status;
        matchingStep.content.meta.files = step.files || [];
      }
    }
  });

  const mainResponse = normalizeAiContent(message?.response);
  if (!mainResponse && chatSteps.length > 0) {
    const lastStep = chatSteps[chatSteps.length - 1];
    if (!lastStep.content) {
      lastStep.content = { meta: {} };
    }
    if (!lastStep.content.meta) {
      lastStep.content.meta = {};
    }
    lastStep.content.meta.isStreaming = true;
  }

  return chatSteps;
}
export const GENERATING_RESPONSE_ANIMATION_TEXT = `...`;
